<script setup>
import { computed, ref } from 'vue';
import { eventTypeMeta } from '../utils/eventMeta';

const props = defineProps({
  events: { type: Array, default: () => [] },
});
defineEmits(['edit', 'newAt']);

const cursor = ref(new Date());
cursor.value.setDate(1);

const monthLabel = computed(() =>
  cursor.value.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
);

const grid = computed(() => {
  const first = new Date(cursor.value);
  const year = first.getFullYear();
  const month = first.getMonth();
  const startWeekday = first.getDay(); // 0 = Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    const date = new Date(year, month, d);
    const iso = date.toISOString().slice(0, 10);
    const dayEvents = props.events.filter(e => e.startsAt && e.startsAt.slice(0, 10) === iso);
    cells.push({ day: d, date, iso, events: dayEvents });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
});

const today = new Date().toISOString().slice(0, 10);

function shift(delta) {
  const d = new Date(cursor.value);
  d.setMonth(d.getMonth() + delta);
  cursor.value = d;
}
const DAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
</script>

<template>
  <div class="rounded-xl border border-n-strong/40 bg-n-background p-4">
    <div class="mb-3 flex items-center justify-between">
      <h3 class="text-sm font-semibold capitalize text-n-slate-12">{{ monthLabel }}</h3>
      <div class="flex gap-1">
        <button
          type="button"
          class="inline-flex size-7 items-center justify-center rounded-md border border-n-strong/40 hover:bg-n-slate-2"
          @click="shift(-1)"
        >
          <span class="i-lucide-chevron-left size-4 text-n-slate-11" />
        </button>
        <button
          type="button"
          class="inline-flex h-7 items-center rounded-md border border-n-strong/40 px-2 text-xs font-medium hover:bg-n-slate-2"
          @click="cursor = new Date(new Date().getFullYear(), new Date().getMonth(), 1)"
        >
          Hoje
        </button>
        <button
          type="button"
          class="inline-flex size-7 items-center justify-center rounded-md border border-n-strong/40 hover:bg-n-slate-2"
          @click="shift(1)"
        >
          <span class="i-lucide-chevron-right size-4 text-n-slate-11" />
        </button>
      </div>
    </div>

    <div class="grid grid-cols-7 gap-1">
      <div
        v-for="(d, i) in DAYS"
        :key="i"
        class="py-1 text-center text-[10px] font-semibold uppercase text-n-slate-11"
      >
        {{ d }}
      </div>

      <div
        v-for="(cell, i) in grid"
        :key="i"
        :class="[
          'min-h-[92px] rounded-md border p-1 text-[11px]',
          cell
            ? cell.iso === today
              ? 'border-blue-400 bg-blue-50'
              : 'border-n-weak bg-n-background hover:bg-n-slate-2'
            : 'border-transparent',
        ]"
        @click="cell && $emit('newAt', cell.iso)"
      >
        <template v-if="cell">
          <div class="mb-1 text-right text-[10px] font-semibold text-n-slate-12">
            {{ cell.day }}
          </div>
          <div class="space-y-0.5">
            <button
              v-for="e in cell.events.slice(0, 3)"
              :key="e.id"
              type="button"
              :class="[
                eventTypeMeta(e.eventType).color,
                'block w-full truncate rounded px-1.5 py-0.5 text-left text-[10px] font-medium text-white',
              ]"
              @click.stop="$emit('edit', e)"
            >
              {{ e.title }}
            </button>
            <div
              v-if="cell.events.length > 3"
              class="text-[10px] text-n-slate-11"
            >
              +{{ cell.events.length - 3 }} mais
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
